import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageBreadcrumb from "@/components/base/PageBreadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { posts } from "@/mocks/posts";
import usePageMeta from "@/hooks/usePageMeta";

export default function CommunityDetailPage() {
  const { id } = useParams();
  const post = posts.find((item) => item.id === id);

  usePageMeta(
    post ? `${post.title} - 挠脚心网站` : "帖子不存在 - 挠脚心网站",
    post
      ? post.excerpt
      : "该帖子不存在或已被删除，返回挠脚心网站社区交流版块查看其他话题。",
    post ? "社区交流,挠脚心社区,话题讨论" : "社区交流,挠脚心网站"
  );

  if (!post) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[1240px] flex-1 flex-col justify-center px-4 py-16 md:px-6">
          <EmptyState
            icon="ri-chat-delete-line"
            title="该帖子不存在或已被删除"
            description="可能链接里的编号有误，也可能这个话题已经被作者关闭。回到社区看看其他讨论吧。"
            actionLabel="返回社区"
            actionTo="/community"
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = posts.filter((item) => item.id !== post.id).slice(0, 4);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1240px] px-4 pb-10 pt-6 md:px-6 md:pt-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <PageBreadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "社区交流", to: "/community" },
                { label: post.title },
              ]}
            />
            <Link
              to="/community"
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-arrow-left-line"></i>
              返回社区
            </Link>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
                <div className="flex flex-wrap items-center gap-2">
                  {post.essence && (
                    <span className="flex items-center gap-1 rounded-md bg-accent-100 px-2 py-1 text-[11px] font-medium text-accent-800">
                      <i className="ri-award-line text-xs"></i>
                      精华话题
                    </span>
                  )}
                  <span className="rounded-md bg-secondary-100 px-2 py-1 text-[11px] text-secondary-900">
                    社区讨论
                  </span>
                  <span className="text-[11px] text-foreground-400">演示内容</span>
                </div>

                <h1 className="mt-3 font-heading text-2xl leading-snug text-foreground-950 md:text-3xl">
                  {post.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-background-100 pb-4 text-xs text-foreground-500 md:text-sm">
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-[11px] font-medium text-primary-800">
                      {post.author.slice(0, 1)}
                    </span>
                    {post.author}
                  </span>
                  <span className="whitespace-nowrap">发布于 {post.date}</span>
                  <span className="whitespace-nowrap">最后更新 {post.updatedAt}</span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-chat-3-line"></i>
                    {post.replyCount} 条回复
                  </span>
                </div>

                <div className="mt-5 flex flex-col gap-4">
                  {post.content.map((paragraph, index) => (
                    <p key={index} className="text-sm leading-loose text-foreground-700 md:text-base">
                      {paragraph}
                    </p>
                  ))}
                </div>

                <div className="mt-6 flex items-start gap-2 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-600">
                  <i className="ri-information-line mt-0.5 text-base text-secondary-600"></i>
                  这是演示站点的虚构帖子，暂不支持真实回复。留言区仅用于展示排版效果。
                </div>
              </div>

              <section className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                      <i className="ri-chat-3-line text-sm"></i>
                    </span>
                    回复 ({post.replies.length})
                  </h2>
                  <span className="text-[11px] text-foreground-400">示范数据</span>
                </div>

                <div className="flex flex-col gap-4">
                  {post.replies.map((reply, index) => (
                    <div key={`${reply.author}-${index}`} className="flex gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-xs font-medium text-secondary-900">
                        {reply.author.slice(0, 1)}
                      </span>
                      <div className="min-w-0 flex-1 rounded-lg bg-background-100/70 px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-medium text-foreground-900">{reply.author}</span>
                          <span className="text-[11px] text-foreground-400">{reply.date}</span>
                          {index === 0 && (
                            <span className="rounded bg-primary-100 px-1.5 py-0.5 text-[10px] text-primary-800">
                              楼主回复
                            </span>
                          )}
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">
                          {reply.content}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-100/60 px-4 py-5 text-center">
                  <p className="text-sm text-foreground-700">演示站点暂不支持发布回复</p>
                  <p className="mt-1 text-xs text-foreground-500">
                    如果你想参与讨论，可以先浏览其他话题，或者读一读社区小公约。
                  </p>
                  <Link
                    to="/community"
                    className="mt-3 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-foreground-900 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-foreground-800"
                  >
                    <i className="ri-arrow-left-line"></i>
                    回到社区列表
                  </Link>
                </div>
              </section>
            </article>

            <aside className="lg:col-span-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-base text-foreground-950">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-800">
                    <i className="ri-links-line text-sm"></i>
                  </span>
                  其他话题
                </h2>
                <div className="flex flex-col divide-y divide-background-100">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/community/detail/${item.id}`}
                      className="group flex flex-col gap-1 py-3 first:pt-0 last:pb-0"
                    >
                      <p className="line-clamp-2-custom text-sm leading-snug text-foreground-900 transition-colors group-hover:text-secondary-700">
                        {item.title}
                      </p>
                      <p className="text-[11px] text-foreground-500">
                        {item.author} · {item.replyCount} 回复
                      </p>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-secondary-200 bg-secondary-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                    <i className="ri-shield-check-line text-sm"></i>
                  </span>
                  讨论请注意
                </h2>
                <ul className="mt-3 flex flex-col gap-2 text-xs leading-relaxed text-foreground-700">
                  <li>· 尊重不同体验，不评判别人的感受。</li>
                  <li>· 不发布露骨或涉及未成年的内容。</li>
                  <li>· 任何互动都以双方自愿、可随时停止为前提。</li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}